import { createContext, useContext, type ReactNode } from 'react'
import { User } from 'lucide-react'
import { IconButton } from '../ui'

/**
 * WP 10.1. What the account button needs from the shell: how to open the
 * sheet, and whether to draw the dot. Context rather than props, because
 * the five screens that draw a `ScreenHead` are not all `App`'s children —
 * Ask's is inside `Thread`, two components down — and threading an
 * `onAccount` through `Butler` into `Thread` would make two components
 * carry a prop neither reads. The sheet's open state and the dot's rule
 * both stay in `App.tsx`, which is the only provider.
 *
 * The default is inert on purpose: a `ScreenHead` rendered outside the
 * provider (the `#/dev/ui` specimen, a test) draws a button that does
 * nothing rather than throwing.
 */
export const AccountContext = createContext<{ open: () => void; dot: boolean }>({
  open: () => {},
  dot: false,
})

/**
 * Every tab's first row: the screen's own content on the left, its own
 * controls, then the account button, always last. WP 10.1 deleted the
 * wordmark bar this button used to sit in (Doug, 28 Sept: "do we need the
 * Schnauzelist header? It takes a lot of room") — the name is the app
 * icon's job, and the button moved down into the row each screen already
 * had.
 *
 * A composition, not a primitive (D5): a flex row, whatever the screen
 * hands it, and one `IconButton`. It takes nothing but the two slots and a
 * class. `className` is how each screen keeps its old header class
 * (`browsebar`, `chat-head`, `lists-head`, `yr-topbar`) as a hook for the
 * render scripts that wait on it — the layout itself is `.screenhead`'s
 * alone, and those classes no longer carry any, so nothing competes with it
 * (CLAUDE.md's `.pitem.tapzone` trap).
 *
 * One row only. Anything under the row — Today's date line, Year's stats —
 * is the screen's own next element, not a second line inside the lead: the
 * row centres its items, and a two-line lead would centre the buttons
 * against both lines instead of beside the first.
 */
export function ScreenHead({ lead, trailing, className }: {
  /** The screen's own first-row content — a heading, or Browse's segmented. */
  lead: ReactNode
  /** The screen's own controls, before the account button. */
  trailing?: ReactNode
  className?: string
}) {
  const { open, dot } = useContext(AccountContext)
  return (
    <div className={className ? `screenhead ${className}` : 'screenhead'}>
      <div className="screenhead__lead">{lead}</div>
      {trailing}
      <IconButton label="Account" className="screenhead__acct" onClick={open}>
        <User aria-hidden="true" />
        {/* N1: exactly one dot, only for a broken adapter or an unread
            alert — `App.tsx`'s `showDot` decides; this only draws it. */}
        {dot && <span className="screenhead__dot" aria-hidden="true" />}
      </IconButton>
    </div>
  )
}
